import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { auth } from '../../utils/firebase.utils';
import SchoolInfo from '../SchoolInfo';
import DraftList from '../DraftList';
import EssayForm from './EssayForm';
import { useUniversityPrompts } from '@/hooks/useUniversityPrompts';
import { useDraftVersionsByUniversity } from '@/services/draftService';
import { useImprovements } from '@/hooks/useImprovementsOnDemand';
import { showApiError } from '@/utils/toast';
import { AnalysisData } from '@/types';

const TextAnalyzer: React.FC = () => {
  const { university } = useParams<{ university: string }>();
  const [data, setData] = useState<AnalysisData | null>(null);
  const [essayText, setEssayText] = useState(data?.essay || '');
  const navigate = useNavigate();
  const [draftVersionId, setDraftVersionId] = useState<number | null>(null);




  const universityId = university ? parseInt(university) : null;
  const { universityData, isLoading: universityDataLoading, error: universityDataError } = useUniversityPrompts(universityId || 0);

  const {
    data: draftVersions = [],
    isLoading: draftsLoading,
    error: draftsError
  } = useDraftVersionsByUniversity(universityId || 0, data?.promptId);

  // Improvements hook
  const {
    data: improvements,
    isLoading: improvementsLoading,
    error: improvementsError,
    refetch: fetchImprovements
  } = useImprovements(draftVersionId ?? undefined);

  const handleSelectPrompt = (
    selectedPrompt: string,
    wordCount: string,
    type: string,
    promptId?: number
  ) => {

    setData({
      prompt: selectedPrompt,
      wordCount: Number(wordCount),
      promptId: promptId,
      universityId: universityId
    })
  };

  const handleAutofill = (selectedDraft: AnalysisData) => {
    setData(selectedDraft);
    setEssayText(selectedDraft.essay || '');
    console.log('Selected draft version ID:', selectedDraft.draftVersionId);

    if (selectedDraft.draftVersionId) {
      setDraftVersionId(selectedDraft.draftVersionId);
      // The useImprovements hook will automatically fetch when draftVersionId changes
    }
  }

  // Navigate to results when improvements are ready
  useEffect(() => {
    if (improvements && improvements.length > 0 && data) {
      console.log('Improvements ready, navigating to results:', improvements);

      navigate(`/result/${data?.universityId}`, {
        state: {
          analysis: { improvements},
          data: data
        }
      });
    }
  }, [improvements, data, navigate]);



  // Show loading state
  if (universityDataLoading) {
    return (
      <div className="flex flex-col lg:flex-row px-4 gap-6 h-full overflow-y-auto">
        <div className="w-full lg:w-1/4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="mb-6">
              <div className="text-center text-gray-900 dark:text-gray-100">Loading university data...</div>
            </div>
            <div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
            </div>
          </div>
        </div>
        <div className="w-full lg:w-1/2 p-4 rounded-lg overflow-y-auto max-h-[530px]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-4 overflow-y-auto short:max-h-[290px] w-full max-w-7xl">
            <div className="text-center">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-3/4 mx-auto"></div>
            </div>
          </div>
        </div>
        <div className="w-full lg:w-1/4 bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 overflow-y-auto max-h-[530px] short:max-h-[400px]">
          <div className="text-center py-10 text-gray-900 dark:text-gray-100">Loading drafts...</div>
        </div>
      </div>
    );
  }

  // Show error state
  if (universityDataError) {
    showApiError(universityDataError);
    return (
      <div className="flex flex-col lg:flex-row px-4 gap-6 h-full overflow-y-auto">
        <div className="w-full lg:w-1/4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="mb-6">
              <div className="text-center text-red-600 dark:text-red-400">Error loading university data</div>
            </div>
            <div>
              <div className="h-4 bg-red-100 dark:bg-red-900/30 rounded"></div>
            </div>
          </div>
        </div>
        <div className="w-full lg:w-1/2 p-4 rounded-lg overflow-y-auto max-h-[530px]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-4 overflow-y-auto short:max-h-[290px] w-full max-w-7xl">
            <div className="text-center">
              <div className="h-4 bg-red-100 dark:bg-red-900/30 rounded mb-2"></div>
              <div className="h-4 bg-red-100 dark:bg-red-900/30 rounded w-3/4 mx-auto"></div>
            </div>
          </div>
        </div>
        <div className="w-full lg:w-1/4 bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 overflow-y-auto max-h-[530px] short:max-h-[400px]">
          <div className="text-center py-10 text-red-600 dark:text-red-400">Error loading drafts</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row px-4 gap-6 h-full overflow-y-auto">
      {university && university !== 'No University Selected' && universityData && (
        <div className="w-full lg:w-1/4">
          <SchoolInfo
            university={university}
            onSelectPrompt={handleSelectPrompt}
            universityData={universityData}
          />
        </div>
      )}
      {/* CENTER - Essay Form */}
      <div className="w-full lg:w-1/2 p-4 rounded-lg overflow-y-auto max-h-[530px]">
        <EssayForm
          data={data || undefined}
          essayText={essayText}
          setEssayText={setEssayText}
        />
      </div>

      <div className="w-full lg:w-1/4 bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 overflow-y-auto max-h-[530px] short:max-h-[400px]">
        {draftsLoading ? (
          <div className="text-center py-10 text-gray-900 dark:text-gray-100">Loading drafts...</div>
        ) : draftVersions.length > 0 ? (
          <>
            <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-gray-100">Your Drafts</h3>
            <DraftList
              drafts={draftVersions}
              autofillDraft={handleAutofill}
              universityId={universityId!}
              prompt={data?.prompt || ''}
            />
          </>
        ) : (
          <div className="text-center py-8">
            <div className="flex flex-col items-center space-y-3">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="text-gray-500 dark:text-gray-400 text-sm">
                No drafts saved yet.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TextAnalyzer;
